/**
 * LessonBoardRenderer.js — THE lesson board card renderer (Task #301).
 *
 * Single importable module consumed by BOTH:
 *   - the live tutor stage (TutorStageController._boardHTML delegates here)
 *   - the lesson-library admin panel preview (lessonAdmin.js)
 *
 * Extracted so the admin preview renders through the SAME trusted templates
 * the stage uses — never a copy. Task #302 restructures the stage surface in
 * parallel: keep this the one shared module both consume.
 *
 * Everything here is pure data → HTML string. Generated content is escaped;
 * board specs never carry executable anything (enforced server-side by the
 * lesson_store sanitizer + banned-pattern lint on every write).
 *
 * Task #348 — "the board writes itself": every text slot of the existing
 * board kinds may now be authored as EITHER a plain string (legacy, forever
 * valid) or an object {text, at?, fx?, id?} — a reveal FRAGMENT. Fragment
 * enumeration lives in boardRevealPlan(); boardHTML() stamps matching
 * `data-frag="<n>"` markers on the SAME slots in the SAME order, so the two
 * can never disagree about what fragment N is. The markers are inert on
 * their own (admin preview / legacy render unchanged) — staging, effects,
 * foley and cues are all runtime concerns owned by TutorStageController +
 * the ONE CueScheduler.
 *
 * Board-level `at`/`fx`/`id` describe the card's PRIMARY block when one
 * exists (equation body / visual svg / completion content / teach figure);
 * when the card has no primary block they act as the default anchor/effect
 * for fragments that don't carry their own. `fx` is a closed enum
 * (REVEAL_FX); anything else is a validation error upstream and treated as
 * "unspecified" here.
 */

import { fractionSVG, boardFigureHTML } from "./FractionWidgets.js";
import { REVEAL_FX, MARK_STYLES, boardMarkTexts } from "./CueScheduler.js";

export function escapeHTML(s) {
  return String(s == null ? "" : s)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

/** A text slot is a string (legacy) or a reveal fragment {text, at?, fx?, id?}. */
export function slotText(v) {
  if (v && typeof v === "object" && !Array.isArray(v)) {
    return v.text == null ? "" : String(v.text);
  }
  return v == null ? "" : String(v);
}

const isObj = (v) => v && typeof v === "object" && !Array.isArray(v);
const fxOf = (v) => (typeof v === "string" && REVEAL_FX.has(v) ? v : null);
const idOf = (v) => (typeof v === "string" && /^[A-Za-z_][A-Za-z0-9_-]{0,31}$/.test(v) ? v : null);

/**
 * Enumerate a board's reveal fragments in DOM order — the single source of
 * truth the runtime stages from. Returns:
 *   { fragments: [{ slot, fx, at, id, container }], cardId }
 *
 *   slot       — human label ("fig" | "title" | "line0" | "body" | …)
 *   fx         — effective effect: own → board default → per-slot default
 *   at         — authored anchor value (null = candidate for auto-staging)
 *   id         — authored fragment id (fires {"after": id} on reveal end)
 *   container  — the OBJECT that carries `at` (identity-matches the cue the
 *                scheduler collected from the same beat), or null
 *   cardId     — board-level id NOT claimed by a primary block: fires when
 *                the whole card has revealed
 *
 * boardHTML() emits data-frag="<index into fragments>" on the same slots in
 * the same order — keep the two in lockstep when touching either.
 */
export function boardRevealPlan(board, opts = {}) {
  const out = { fragments: [], cardId: null };
  if (!isObj(board)) return out;
  const k = board.kind;
  const bAt = board.at !== undefined ? board.at : null;
  const bFx = fxOf(board.fx);
  const bId = idOf(board.id);

  let figHTML = opts.figHTML;
  if (figHTML === undefined) {
    try { figHTML = boardFigureHTML(board) || ""; } catch (_) { figHTML = ""; }
  }

  // Which slot is the card's primary block (claims board-level at/fx/id)?
  const primarySlot =
    k === "equation" ? "body" :
    k === "visual" ? "svg" :
    k === "completion" ? "content" :
    k === "plate" ? "plate" :
    (figHTML ? "fig" : null);

  const push = (slot, own) => {
    const ownObj = isObj(own) ? own : null;
    const isPrimary = slot === primarySlot;
    let at = null;
    let container = null;
    if (ownObj && ownObj.at !== undefined) {
      at = ownObj.at; container = ownObj;
    } else if (isPrimary && bAt !== null) {
      at = bAt; container = board;
    } else if (!primarySlot && bAt !== null) {
      // No primary block → board-level anchor is the card-wide default.
      at = bAt; container = board;
    }
    const defFx = slot === "fig" || slot === "svg" || slot === "body" ? "draw"
      : slot === "row" || slot === "content" || slot === "plate" ? "fade" : "write";
    // Board-level fx has the SAME scope as board-level `at`/`id`: it belongs
    // to the primary block when one exists; only no-primary cards use it as
    // the card-wide default. Title/caption keep their per-slot defaults.
    const bFxHere = isPrimary || !primarySlot ? bFx : null;
    const fx = (ownObj && fxOf(ownObj.fx)) || bFxHere || defFx;
    // fxAuthored: the author PICKED a verb (own or board-level-in-scope).
    // This is what opts bespoke kinds (completion/leagues) into staging and
    // what keeps a verb performing even without alignment; a defaulted fx
    // never does.
    const fxAuthored = !!((ownObj && fxOf(ownObj.fx)) || bFxHere);
    const id = (ownObj && idOf(ownObj.id)) || (isPrimary ? bId : null);
    out.fragments.push({ slot, fx, fxAuthored, at, id, container });
  };

  if (k === "heading") {
    if (figHTML) push("fig", null);
    if (slotText(board.kicker)) push("kicker", board.kicker);
    if (slotText(board.title)) push("title", board.title);
    (board.lines || []).forEach((l, i) => push(`line${i}`, l));
  } else if (k === "equation") {
    if (figHTML) push("fig", null);
    if (slotText(board.title)) push("title", board.title);
    push("body", isObj(board.latex) ? board.latex : null);
    if (slotText(board.caption)) push("caption", board.caption);
  } else if (k === "visual") {
    if (slotText(board.title)) push("title", board.title);
    push("svg", null);
    if (slotText(board.caption)) push("caption", board.caption);
  } else if (k === "definition") {
    if (figHTML) push("fig", null);
    push("term", board.term);
    push("meaning", board.meaning);
  } else if (k === "diagram") {
    if (slotText(board.title)) push("title", board.title);
    (board.steps || []).forEach((s, i) => push(`step${i}`, s));
  } else if (k === "completion") {
    push("content", null);
  } else if (k === "plate") {
    // Task #352 — primary sources on the board. The plate figure is the
    // primary block; the caption slot exists whenever there is caption TEXT
    // to show (authored override, else the curated manifest caption riding
    // in on the server-stamped `_resolved`). Degraded render (no
    // `_resolved`) keeps the same slot order — the plate slot becomes the
    // caption card, so plan ↔ DOM lockstep holds on every path.
    if (slotText(board.title)) push("title", board.title);
    push("plate", null);
    const res = isObj(board._resolved) ? board._resolved : null;
    if (slotText(board.caption) || (res && res.caption)) push("caption", board.caption);
  } else if (k === "leagues") {
    if (slotText(board.title)) push("title", board.title);
    const rows = (board.rows || []).slice()
      .sort((a, b) => (Number(b.xp) || 0) - (Number(a.xp) || 0));
    rows.forEach((r) => {
      // Rows are already objects — at/fx/id ride inline on the row itself;
      // an anchor-less row inherits the board-level anchor exactly like
      // push()'s no-primary-block rule (board `at` = card-wide default).
      const ownObj = isObj(r) ? r : null;
      let at = null;
      let container = null;
      if (ownObj && ownObj.at !== undefined) {
        at = ownObj.at; container = ownObj;
      } else if (!primarySlot && bAt !== null) {
        at = bAt; container = board;
      }
      // Rows are never the primary block — board-level fx reaches them only
      // on a no-primary card (same scope rule as push()).
      const bFxRow = primarySlot ? null : bFx;
      out.fragments.push({
        slot: "row",
        fx: (ownObj && fxOf(ownObj.fx)) || bFxRow || "fade",
        fxAuthored: !!((ownObj && fxOf(ownObj.fx)) || bFxRow),
        at,
        id: (ownObj && idOf(ownObj.id)) || null,
        container,
      });
    });
  } else {
    // points / example / summary → the generic lines renderer
    if (figHTML) push("fig", null);
    if (slotText(board.title)) push("title", board.title);
    (board.lines || []).forEach((l, i) => push(`line${i}`, l));
  }

  if (bId && primarySlot === null) out.cardId = bId;
  return out;
}

/** Stamp data-frag="<i>" into the FIRST tag of an HTML string (figure/KaTeX
 * roots we don't build ourselves). */
function stampFrag(html, i) {
  if (!html) return html;
  return String(html).replace(/^\s*<([a-zA-Z][^\s/>]*)/,
    (m) => `${m} data-frag="${i}"`);
}

// The ONLY class shape the KaTeX trust() callback accepts — a mark stamp we
// wrote ourselves. Authored latex can never smuggle \htmlClass through.
const MARK_CLASS_RE = /^ts-mark-t ts-mk-\d+ ts-mks-(hl|term|warn|dim)$/;

/**
 * Task #350 — resolve a board's authored `marks` to concrete stamp ranges.
 * Returns { marks: [{ mi, style, target, slot, start, end, container }] }.
 *
 *   mi         — index into board.marks (the stamp class is `ts-mk-<mi>`)
 *   slot       — boardMarkTexts slot name the target resolved to ("latex" =
 *                the equation SOURCE — wrapped via \htmlClass, not sliced)
 *   start/end  — character range of the FIRST occurrence in that slot
 *   container  — the authored mark dict itself: it carries `at`, so its
 *                IDENTITY matches the cue container the scheduler collected
 *                from the same beat (exactly like reveal fragments)
 *
 * Marks whose target is absent, ambiguous (≥2 slots), or overlapping an
 * earlier mark's range are SKIPPED here — validation owns the loud error
 * (lintBoardMarks); the renderer must never guess. boardHTML() stamps
 * matching `.ts-mk-<mi>` spans; the stamps are inert until the runtime
 * fires them (admin preview / legacy render unchanged).
 */
export function boardMarkPlan(board) {
  const out = { marks: [] };
  if (!isObj(board) || !Array.isArray(board.marks)) return out;
  const texts = boardMarkTexts(board);
  const taken = new Map(); // slot → [[start, end]] claimed ranges
  board.marks.forEach((m, mi) => {
    if (!isObj(m)) return;
    const style = typeof m.mark === "string" && MARK_STYLES.has(m.mark) ? m.mark : null;
    const target = typeof m.target === "string" && m.target ? m.target : null;
    if (!style || !target) return;
    const hits = texts.filter((s) => s.text.includes(target));
    if (hits.length !== 1) return;
    const { slot, text } = hits[0];
    const start = text.indexOf(target);
    const end = start + target.length;
    const ranges = taken.get(slot) || [];
    if (ranges.some(([s, e]) => start < e && end > s)) return;
    ranges.push([start, end]);
    taken.set(slot, ranges);
    out.marks.push({ mi, style, target, slot, start, end, container: m });
  });
  return out;
}

/**
 * Render one board card's inner HTML. Identical behavior to the pre-#301
 * TutorStageController._boardHTML — that method now delegates here.
 */
export function boardHTML(board, opts = {}) {
  const esc = opts.esc || escapeHTML;
  const k = board.kind;
  // v2 handoff — teach-board figures (graph / graphs+toggle / scene /
  // diagram / scene3d) render ON the card like Brilliant's teach pages.
  let fig = "";
  try { fig = boardFigureHTML(board) || ""; } catch (_) { fig = ""; }

  // Task #348 — fragment cursor. The plan enumerates slots in DOM order;
  // frag() hands out matching data-frag markers in that same order.
  const plan = boardRevealPlan(board, { figHTML: fig });
  let cursor = 0;
  const frag = () => ` data-frag="${cursor++}"`;
  const skip = () => { cursor++; };

  // Task #350 — mark stamping. The plan resolved each authored mark to ONE
  // slot + range; mtext() wraps that range in an inert .ts-mark-t span in the
  // SAME pass that stamps data-frag (slot names in lockstep with
  // boardMarkTexts). "latex" targets are wrapped inside the KaTeX source in
  // the equation branch instead.
  const mPlan = boardMarkPlan(board);
  const marksBySlot = new Map();
  for (const m of mPlan.marks) {
    if (m.slot === "latex") continue;
    const arr = marksBySlot.get(m.slot) || [];
    arr.push(m);
    marksBySlot.set(m.slot, arr);
  }
  const mtext = (slot, text) => {
    const t = String(text == null ? "" : text);
    const ms = marksBySlot.get(slot);
    if (!ms || !ms.length) return esc(t);
    const sorted = ms.slice().sort((a, b) => a.start - b.start);
    let html = "";
    let prev = 0;
    for (const m of sorted) {
      if (m.start < prev || m.end > t.length) continue; // never mis-slice
      html += esc(t.slice(prev, m.start)) +
        '<span class="ts-mark-t ts-mk-' + m.mi + " ts-mks-" + m.style +
        '" data-mark="' + m.style + '">' + esc(t.slice(m.start, m.end)) + "</span>";
      prev = m.end;
    }
    return html + esc(t.slice(prev));
  };

  const titled = () => slotText(board.title)
    ? '<h3 class="ts-card-title"' + frag() + ">" + mtext("title", slotText(board.title)) + "</h3>" : "";

  if (k === "heading") {
    const figH = fig ? stampFrag(fig, (skip(), cursor - 1)) : "";
    const kick = slotText(board.kicker)
      ? '<span class="ts-kicker"' + frag() + ">" + mtext("kicker", slotText(board.kicker)) + "</span>" : "";
    const tt = titled();
    const sub = (board.lines || []).map((l, i) =>
      '<p class="ts-heading-sub"' + frag() + ">" + mtext(`line${i}`, slotText(l)) + "</p>").join("");
    return figH + '<div class="ts-heading">' + kick + tt + sub + "</div>";
  }
  if (k === "equation") {
    const figH = fig ? stampFrag(fig, (skip(), cursor - 1)) : "";
    const tt = titled();
    // Task #251 — render real math with KaTeX (page-level, self-hosted). The
    // global is loaded by the page, not bundled. Guarded fallback to the
    // escaped LaTeX string if KaTeX is missing or the input won't parse.
    const latex = slotText(board.latex);
    const hasKatex = typeof window !== "undefined" && window.katex &&
      typeof window.katex.renderToString === "function";
    // Task #350 — KaTeX sub-expression addressing: wrap each target range of
    // the SOURCE in \htmlClass{ts-mark-t …} (descending start order so
    // earlier ranges stay valid; ranges are disjoint by plan). trust() is
    // scoped to EXACTLY our class shape, and throwOnError:true means ANY
    // parse failure falls back to the plain unmarked render below — the
    // equation itself never degrades because of a mark.
    const eqMarks = mPlan.marks.filter((m) => m.slot === "latex")
      .sort((a, b) => b.start - a.start);
    let body = null;
    if (hasKatex && eqMarks.length) {
      let wrapped = latex;
      for (const m of eqMarks) {
        wrapped = wrapped.slice(0, m.start) +
          "\\htmlClass{ts-mark-t ts-mk-" + m.mi + " ts-mks-" + m.style + "}{" +
          wrapped.slice(m.start, m.end) + "}" + wrapped.slice(m.end);
      }
      try {
        body = window.katex.renderToString(wrapped, {
          displayMode: true,
          throwOnError: true,
          trust: (ctx) => (ctx.command === "\\htmlClass" || ctx.command === "htmlClass") &&
            MARK_CLASS_RE.test(String(ctx.class || "")),
          strict: (code) => (code === "htmlExtension" ? "ignore" : "warn"),
        });
      } catch (_) { body = null; }
    }
    if (body == null) {
      try {
        body = hasKatex
          ? window.katex.renderToString(latex, {
            displayMode: true, throwOnError: false, trust: false,
          })
          : esc(latex);
      } catch (_) {
        body = esc(latex);
      }
    }
    const eq = '<div class="ts-equation"' + frag() + ">" + body + "</div>";
    const cap = slotText(board.caption)
      ? '<p class="ts-eq-cap"' + frag() + ">" + mtext("caption", slotText(board.caption)) + "</p>" : "";
    return figH + tt + eq + cap;
  }
  if (k === "visual") {
    // Task #251 — a crisp fraction visual (pie / bar / number line).
    const tt = titled();
    let svg = "";
    try { svg = fractionSVG(board.visual || board); } catch (_) { svg = ""; }
    const svgH = stampFrag(svg, (skip(), cursor - 1));
    const cap = slotText(board.caption)
      ? '<p class="ts-eq-cap"' + frag() + ">" + mtext("caption", slotText(board.caption)) + "</p>" : "";
    return tt + svgH + cap;
  }
  if (k === "definition") {
    const figH = fig ? stampFrag(fig, (skip(), cursor - 1)) : "";
    return figH + '<div class="ts-def"><span class="ts-def-term"' + frag() + ">" +
      mtext("term", slotText(board.term)) + '</span><p class="ts-def-meaning"' + frag() + ">" +
      mtext("meaning", slotText(board.meaning)) + "</p></div>";
  }
  if (k === "diagram") {
    const tt = titled();
    // The arrow between step k-1 and step k reveals WITH step k, so the pair
    // shares one data-frag group.
    const steps = board.steps || [];
    const chips = steps.map((s, i) => {
      const fi = cursor++;
      const arrow = i > 0 ? '<span class="ts-arrow" data-frag="' + fi + '">→</span>' : "";
      return arrow + '<span class="ts-step" style="--d:' + i + '" data-frag="' + fi + '">' +
        mtext(`step${i}`, slotText(s)) + "</span>";
    }).join("");
    return tt + '<div class="ts-diagram">' + chips + "</div>";
  }
  if (k === "completion") {
    const kick = slotText(board.kicker)
      ? '<span class="ts-kicker">' + mtext("kicker", slotText(board.kicker)) + "</span>" : "";
    // Player ceremony Iteration 2 — `scope: lesson|level|course` (default
    // lesson). The stats are RUNTIME-filled by the player from real results:
    // XP, accuracy and the scoped lesson tally (lesson → "1 Lesson"; level /
    // course → "N/N Lessons"). Labels default to the lesson-scope wording so
    // the card is never blank before the player fills it.
    const scope = board.scope === "level" || board.scope === "course" ? board.scope : "lesson";
    const xpLabel = scope === "lesson" ? "XP earned" : (scope === "level" ? "Level XP" : "Course XP");
    return '<div class="ts-complete" data-scope="' + scope + '"' + frag() + ">" +
      '<div class="ts-complete-badge"><svg viewBox="0 0 24 24">' +
      '<polyline points="20 6 9 17 4 12"/></svg></div>' + kick +
      '<h3 class="ts-complete-title">' +
       (slotText(board.title) ? mtext("title", slotText(board.title)) : esc("Practice complete")) + "</h3>" +
      '<div class="ts-complete-stats">' +
      '<div class="ts-stat"><span class="ts-stat-num" data-xp-total>0</span>' +
      '<span class="ts-stat-label" data-xp-label>' + xpLabel + '</span></div>' +
      '<div class="ts-stat"><span class="ts-stat-num" data-acc>\u2014</span>' +
      '<span class="ts-stat-label">Accuracy</span></div>' +
      '<div class="ts-stat"><span class="ts-stat-num" data-lessons>' +
      (scope === "lesson" ? "1" : "\u2014") + '</span>' +
      '<span class="ts-stat-label" data-lessons-label>' +
      (scope === "lesson" ? "Lesson" : "Lessons") + '</span></div>' +
      '</div><p class="ts-completion-bonus" data-completion-bonus hidden>' +
      '<span data-bonus-total>+0 XP</span> \u00b7 Lesson-completion bonus</p>' +
      '<p class="ts-result-note" data-result-note></p></div>';
  }
  if (k === "plate") {
    // Task #352 — primary sources on the board. The board carries only an
    // ASSET ID; everything visual (src/alt/regions/curated caption) rides in
    // on the server-stamped `_resolved` from the legend's signed manifest.
    // Degradation ladder: no `_resolved` (asset unresolvable at serve time)
    // → the caption card renders in the plate slot; image fails to LOAD at
    // play → the inline onerror flips .is-broken and CSS swaps the same
    // caption card in (the lesson never breaks).
    const res = isObj(board._resolved) ? board._resolved : null;
    const tt = titled();
    const asset = typeof board.asset === "string" ? board.asset : "";
    const aid = asset.includes("/") ? asset.slice(asset.indexOf("/") + 1) : asset;
    const prettyTitle = slotText(board.title) || (res && res.title) ||
      aid.replace(/-/g, " ");
    const capText = slotText(board.caption) || (res && res.caption) || "";
    const fallback = '<div class="ts-plate-fallback">' +
      '<svg class="ts-plate-emblem" viewBox="0 0 24 24" aria-hidden="true">' +
      '<rect x="3" y="3" width="18" height="18" rx="2"/>' +
      '<circle cx="9" cy="9" r="2"/><path d="M21 15l-5-5-11 11"/></svg>' +
      '<span class="ts-plate-fb-kicker">' + esc("From the archive") + "</span>" +
      '<span class="ts-plate-fb-title">' + esc(prettyTitle) + "</span>" +
      (capText ? '<span class="ts-plate-fb-cap">' + esc(capText) + "</span>" : "") +
      "</div>";
    let inner;
    if (res && res.src) {
      const ar = res.w > 0 && res.h > 0 ? (res.w / res.h).toFixed(6) : "1.5";
      inner = '<div class="ts-plate-viewport" style="--plate-ar:' + ar + '">' +
        '<img class="ts-plate-img" src="' + esc(res.src) + '" alt="' + esc(res.alt || "") +
        '" draggable="false" decoding="async" ' +
        "onerror=\"this.closest('.ts-plate').classList.add('is-broken')\"/>" +
        "</div>" + fallback;
    } else {
      inner = fallback;
    }
    const plateH = '<figure class="ts-plate' + (res && res.src ? "" : " is-missing") +
      '"' + frag() + ' data-plate-asset="' + esc(asset) + '">' + inner + "</figure>";
    const cap = capText
      ? '<p class="ts-plate-cap"' + frag() + ">" + mtext("caption", capText) + "</p>" : "";
    return tt + plateH + cap;
  }
  if (k === "leagues") {
    const tt = titled();
    const rows = (board.rows || []).slice()
      .sort((a, b) => (Number(b.xp) || 0) - (Number(a.xp) || 0));
    const items = rows.map((r, i) =>
      '<li class="ts-league-row' + (r.you ? " is-you" : "") + '" style="--d:' + i + '"' + frag() + ">" +
      '<span class="ts-league-rank">' + (i + 1) + "</span>" +
      '<span class="ts-league-name">' + esc(r.name || "") + "</span>" +
      '<span class="ts-league-xp">' + (parseInt(r.xp, 10) || 0) + " XP</span></li>").join("");
    return tt + '<ul class="ts-leagues">' + items + "</ul>";
  }
  // points / example / summary => staged reveal of lines
  const figH = fig ? stampFrag(fig, (skip(), cursor - 1)) : "";
  const tt = titled();
  const lines = (board.lines || []).map((ln, i) =>
    '<li class="ts-line" style="--d:' + i + '"' + frag() + ">" + mtext(`line${i}`, slotText(ln)) + "</li>").join("");
  const cls = k === "summary" ? "ts-list ts-list-summary" : "ts-list";
  return figH + tt + '<ul class="' + cls + '">' + lines + "</ul>";
}
